/**
 * Profile Switcher Modal
 * 
 * Modal component for switching between profiles without logging out.
 * Matches the Flutter TV implementation pattern.
 * 
 * Legacy CSS: Uses table layout and inline-block for old Chromium compatibility.
 */
import React, { useEffect, useCallback } from "react";
import styled from "styled-components";
import {
    useFocusable,
    FocusContext,
    setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import toast from "react-hot-toast";
import httpService from "../../services/http.service";
import { getAuthApiRoute } from "../../utils/api.utils";
import {
    PROFILE_COLORS,
    PROFILE_FONT_SIZES,
    PROFILE_SPACING,
    PROFILE_RADIUS,
    PROFILE_SHADOWS,
    FONT_FAMILY,
} from "./styles/profileStyles";

// ============================================================================
// JWT DECODE HELPER (without verification)
// ============================================================================

/**
 * Decode JWT payload without verification.
 * Used to extract profile ID from access token.
 * Matches Flutter TokenStorage.getProfileIdFromToken() behavior.
 */
function decodeJwtPayload(token) {
    try {
        if (!token || typeof token !== 'string') {
            return null;
        }
        var parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }
        // Decode base64url payload (second part)
        var payload = parts[1];
        // Replace base64url characters with base64
        payload = payload.replace(/-/g, '+').replace(/_/g, '/');
        // Add padding if needed
        var pad = payload.length % 4;
        if (pad) {
            payload += '='.repeat(4 - pad);
        }
        var decoded = atob(payload);
        return JSON.parse(decoded);
    } catch (error) {
        console.warn('[ProfileSwitch] JWT decode error:', error);
        return null;
    }
}

/**
 * Get profile ID from stored access token.
 */
function getProfileIdFromToken() {
    var token = localStorage.getItem('webTvUserToken');
    if (!token) return null;

    var payload = decodeJwtPayload(token);
    if (!payload) return null;

    // Profile ID is typically stored as 'profileId' or 'profile_id' in JWT
    return payload.profileId || payload.profile_id || payload.pid || null;
}

// ============================================================================
// PWA RESTART HELPER
// ============================================================================

/**
 * Perform a complete PWA restart to re-fetch all private data.
 * Matches the Android TV AppRestartHelper.softRestartForTvProfileSwitch() behavior.
 * 
 * Steps:
 * 1. Clear all caches (CacheStorage API)
 * 2. Unregister service workers
 * 3. Clear user-specific cached data from localStorage
 * 4. Navigate to root with cache-bust parameter
 */
async function performPwaRestart() {
    try {
        // 1. Clear all caches
        if ('caches' in window) {
            var cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(function (cacheName) {
                    return caches.delete(cacheName);
                })
            );
            console.log('[ProfileSwitch] Cleared all caches');
        }

        // 2. Unregister all service workers
        if ('serviceWorker' in navigator) {
            var registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(
                registrations.map(function (registration) {
                    return registration.unregister();
                })
            );
            console.log('[ProfileSwitch] Unregistered service workers');
        }

        // 3. Clear user-specific cached data from localStorage
        // Keep only auth tokens, remove cached private data
        var keysToRemove = [];
        for (var i = 0; i < localStorage.length; i++) {
            var key = localStorage.key(i);
            // Remove cached data but keep tokens
            if (key && (
                key.includes('cache') ||
                key.includes('Cache') ||
                key.includes('privateData') ||
                key.includes('userData') ||
                key.includes('profile_') ||
                key.includes('showcase')
            )) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(function (key) {
            localStorage.removeItem(key);
        });
        console.log('[ProfileSwitch] Cleared cached local data');

    } catch (error) {
        console.warn('[ProfileSwitch] Error clearing caches:', error);
    }

    // 4. Navigate to root with cache-bust parameter for complete restart
    var bustParam = 'profile_switch=' + Date.now();
    window.location.href = '/?' + bustParam;
}

// ============================================================================
// STYLED COMPONENTS (Legacy TV compatible)
// ============================================================================

var Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 1000;
  display: ${function (props) { return props.$open ? "table" : "none"; }};
  width: 100%;
  height: 100%;
  table-layout: fixed;
`;

var OverlayCell = styled.div`
  display: table-cell;
  vertical-align: middle;
  text-align: center;
`;

var ModalCard = styled.div`
  display: inline-block;
  width: 90%;
  max-width: 1280px;
  background: linear-gradient(
    135deg,
    ${PROFILE_COLORS.cardGradientStart} 0%,
    ${PROFILE_COLORS.cardGradientEnd} 100%
  );
  border-radius: ${PROFILE_RADIUS.lg};
  border: 1px solid ${PROFILE_COLORS.border};
  padding: ${PROFILE_SPACING.xl};
  text-align: center;
  direction: rtl;
`;

var ModalTitle = styled.div`
  color: ${PROFILE_COLORS.text};
  font-size: ${PROFILE_FONT_SIZES.title};
  font-weight: 700;
  font-family: ${FONT_FAMILY};
  margin-bottom: ${PROFILE_SPACING.lg};
`;

var ProfilesContainer = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding: ${PROFILE_SPACING.md} 0;
  margin-bottom: ${PROFILE_SPACING.lg};
`;

var ProfileCardWrapper = styled.div`
  display: inline-block;
  vertical-align: top;
  margin: 0 ${PROFILE_SPACING.sm};
`;

var ProfileCardStyled = styled.div`
  display: inline-block;
  width: 300px;
  height: 250px;
  padding: ${PROFILE_SPACING.md};
  background: ${function (props) {
        return props.$focused
            ? PROFILE_COLORS.accentLight
            : props.$isSelected
                ? "rgba(239, 68, 68, 0.1)"
                : PROFILE_COLORS.sectionBg;
    }};
  border: ${function (props) {
        return props.$focused
            ? PROFILE_SHADOWS.focusBorder()
            : props.$isSelected
                ? "2px solid " + PROFILE_COLORS.accent
                : "2px solid transparent";
    }};
  border-radius: ${PROFILE_RADIUS.md};
  cursor: pointer;
  text-align: center;
`;

var ProfileAvatar = styled.div`
  width: 72px;
  height: 72px;
  margin: 0 auto ${PROFILE_SPACING.sm};
  border-radius: 50%;
  background: ${function (props) {
        return props.$hasImage
            ? "url(" + props.$imageUrl + ")"
            : "linear-gradient(135deg, #555 0%, #333 100%)";
    }};
  background-size: cover;
  background-position: center;
  border: ${function (props) {
        return props.$isSelected
            ? "3px solid " + PROFILE_COLORS.accent
            : "2px solid rgba(255, 255, 255, 0.3)";
    }};
  display: table;
  table-layout: fixed;
`;

var AvatarInner = styled.div`
  display: table-cell;
  vertical-align: middle;
  text-align: center;
  color: rgba(255, 255, 255, 0.9);
  font-size: 24px;
  font-weight: 600;
`;

var ProfileName = styled.div`
  color: ${PROFILE_COLORS.text};
  font-size: ${PROFILE_FONT_SIZES.small};
  font-weight: 600;
  font-family: ${FONT_FAMILY};
  margin-bottom: ${PROFILE_SPACING.xs};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

var ActiveBadge = styled.div`
  display: inline-block;
  background: ${PROFILE_COLORS.accentLight};
  color: ${PROFILE_COLORS.accent};
  font-size: ${PROFILE_FONT_SIZES.tiny};
  font-weight: 700;
  font-family: ${FONT_FAMILY};
  padding: 4px 12px;
  border-radius: 8px;
  margin-top: ${PROFILE_SPACING.xs};
`;

var AgeLabel = styled.div`
  color: ${PROFILE_COLORS.textMuted};
  font-size: ${PROFILE_FONT_SIZES.tiny};
  font-family: ${FONT_FAMILY};
  margin-top: 4px;
`;

var CancelButton = styled.button`
  display: block;
  width: 700px;
  margin: 0 auto;
  background: ${PROFILE_COLORS.buttonBg};
  border: ${function (props) {
        return props.$focused
            ? PROFILE_SHADOWS.focusBorder()
            : PROFILE_SHADOWS.none;
    }};
  border-radius: ${PROFILE_RADIUS.md};
  padding: ${PROFILE_SPACING.md} ${PROFILE_SPACING.xl};
  color: ${PROFILE_COLORS.textSecondary};
  font-size: ${PROFILE_FONT_SIZES.body};
  font-family: ${FONT_FAMILY};
  cursor: pointer;
`;

var LoadingSpinner = styled.div`
  display: inline-block;
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: ${PROFILE_COLORS.accent};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ============================================================================
// AGE RANGE HELPER
// ============================================================================

function getAgeRangeLabel(age) {
    if (age === 120 || age > 100) {
        return "بزرگسال";
    } else if (age && age > 0) {
        return "تا " + age + " سال";
    }
    return null;
}

// ============================================================================
// FOCUSABLE PROFILE CARD
// ============================================================================

function FocusableProfileCard({
    profile,
    isSelected,
    onSelect,
    focusKey,
    isLoading,
}) {
    var { ref, focused } = useFocusable({
        focusKey: focusKey,
        onEnterPress: function () {
            if (!isLoading && !isSelected) {
                onSelect(profile._id);
            }
        },
    });

    var displayName = profile.fullName || profile.name || "پروفایل";
    var hasImage = profile.profileImage && profile.profileImage.length > 0;
    var initial = displayName.charAt(0) || "U";
    var ageLabel = getAgeRangeLabel(profile.ageRange);

    return (
        <ProfileCardWrapper>
            <ProfileCardStyled
                ref={ref}
                onClick={function () {
                    if (!isLoading && !isSelected) {
                        onSelect(profile._id);
                    }
                }}
                $focused={focused}
                $isSelected={isSelected}
            >
                <ProfileAvatar
                    $hasImage={hasImage}
                    $imageUrl={profile.profileImage}
                    $isSelected={isSelected}
                >
                    {!hasImage && <AvatarInner>{initial}</AvatarInner>}
                </ProfileAvatar>
                <ProfileName>{displayName}</ProfileName>
                {ageLabel && <AgeLabel>{ageLabel}</AgeLabel>}
                {isSelected && <ActiveBadge>فعال</ActiveBadge>}
            </ProfileCardStyled>
        </ProfileCardWrapper>
    );
}

// ============================================================================
// FOCUSABLE CANCEL BUTTON
// ============================================================================

function FocusableCancelButton({ onPress }) {
    var { ref, focused } = useFocusable({
        focusKey: "profile-switcher-cancel",
        onEnterPress: onPress,
    });

    return (
        <CancelButton ref={ref} onClick={onPress} $focused={focused}>
            بازگشت
        </CancelButton>
    );
}

// ============================================================================
// PROFILE SWITCHER MODAL
// ============================================================================

function ProfileSwitcherModal({
    open,
    profiles,
    currentProfileId,
    onClose,
    onSwitchComplete,
}) {
    var [isLoading, setIsLoading] = React.useState(false);
    var { ref, focusKey } = useFocusable({
        focusKey: "PROFILE_SWITCHER_MODAL",
        isFocusBoundary: true,
    });

    // Get actual current profile ID from JWT token (source of truth)
    // Fallback to prop value if token decode fails
    var actualCurrentProfileId = getProfileIdFromToken() || currentProfileId;

    // Set initial focus when modal opens
    useEffect(
        function () {
            if (open && profiles && profiles.length > 0) {
                // Find index of current profile for initial focus
                var currentIndex = profiles.findIndex(function (p) {
                    return p._id === actualCurrentProfileId;
                });
                if (currentIndex === -1) currentIndex = 0;

                setTimeout(function () {
                    setFocus("profile-card-" + currentIndex);
                }, 150);
            }
        },
        [open, profiles, actualCurrentProfileId]
    );

    // Handle ESC key to close
    useEffect(
        function () {
            if (!open) return;

            function handleKeyDown(e) {
                if (e.key === "Escape" || e.key === "Back" || e.keyCode === 10009) {
                    onClose();
                }
            }

            document.addEventListener("keydown", handleKeyDown);
            return function () {
                document.removeEventListener("keydown", handleKeyDown);
            };
        },
        [open, onClose]
    );

    // Handle profile switch
    var handleSelectProfile = useCallback(
        async function (profileId) {
            if (isLoading) return;

            try {
                setIsLoading(true);

                var response = await httpService.post(
                    getAuthApiRoute("switch-profile"),
                    { profileId: profileId }
                );

                if (response.status === 200) {
                    var data = response.data.data || response.data;
                    var accessToken = data.accessToken;
                    var refreshToken = data.refreshToken;

                    if (!accessToken) {
                        toast.error("خطا در دریافت توکن");
                        setIsLoading(false);
                        return;
                    }

                    // Update tokens
                    localStorage.setItem("webTvUserToken", accessToken);
                    if (refreshToken) {
                        localStorage.setItem("webTvrefreshToken", refreshToken);
                    }

                    toast.success("پروفایل با موفقیت تغییر کرد");

                    // Close modal
                    onClose();

                    // Perform complete PWA restart to re-fetch all private data
                    // This matches the Android TV AppRestartHelper.softRestartForTvProfileSwitch() behavior
                    performPwaRestart();
                } else {
                    toast.error(response.data?.message || "خطا در تغییر پروفایل");
                    setIsLoading(false);
                }
            } catch (error) {
                var errorMsg =
                    error.response?.data?.message || error.message || "خطا در تغییر پروفایل";
                toast.error(errorMsg);
                setIsLoading(false);
            }
        },
        [isLoading, onClose, onSwitchComplete]
    );

    if (!open) return null;

    return (
        <FocusContext.Provider value={focusKey}>
            <Overlay $open={open} ref={ref}>
                <OverlayCell>
                    <ModalCard>
                        <ModalTitle>تغییر پروفایل</ModalTitle>

                        {isLoading ? (
                            <div style={{ padding: "40px 0" }}>
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <>
                                <ProfilesContainer>
                                    {profiles &&
                                        profiles.map(function (profile, index) {
                                            var isSelected = profile._id === actualCurrentProfileId;
                                            return (
                                                <FocusableProfileCard
                                                    key={profile._id}
                                                    focusKey={"profile-card-" + index}
                                                    profile={profile}
                                                    isSelected={isSelected}
                                                    onSelect={handleSelectProfile}
                                                    isLoading={isLoading}
                                                />
                                            );
                                        })}
                                </ProfilesContainer>

                                <FocusableCancelButton onPress={onClose} />
                            </>
                        )}
                    </ModalCard>
                </OverlayCell>
            </Overlay>
        </FocusContext.Provider>
    );
}

export default ProfileSwitcherModal;
